import React, { useMemo, useRef, useCallback, useState } from 'react';
import {
  Modal,
  ModalFooter,
  Button,
  Box,
  Input,
  Text,
  FormField,
  Form,
  DatePicker,
  Select,
  Heading,
} from '../ui';
import { useNotify } from '../notifications/ToastNotifications';
import type { UseFormReturn } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import type { ModalProps } from '../ui/Modal/Modal';
import type { ScenarioOrder } from '../../lib/planningData';
import type { BatchEditFields } from './BatchEditCargoesSchema';
import { batchEditFieldsSchema } from './BatchEditCargoesSchema';
import { BatchEditContractSelect } from './BatchEditContractSelect';
import { zodResolver } from '@hookform/resolvers/zod';
import LocationSelect from '../LocationSelect';
import { compact } from 'lodash';
import type { OrderPatch } from '../../graphql';
import {
  useCreateChartererMutation,
  useVcAndOrdersMutation,
  useVoyageCharterFormQuery,
} from '../../graphql';
import AutoCompleteSelect from '../AutoCompleteSelect';
import { reducedUnits } from '../../constants/cargoUnits';
import { useCurrentUser } from '../../state/zustand';
import { valueOrNull } from '../../lib/zod-util';
import loadingUnitOptions from '../../constants/loadingUnits';
import { marginUnits } from '../../constants/margin';
import { DEFAULT_LAYCAN_DAYS } from '../voyageCharter/voyageCharter';
import CreatableSelect from 'react-select/creatable';
import { nodesToOptions, reactSelectStyles } from '../../lib/form-util';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import type { ContractDetailsSchema } from '../voyageCharter/schema';
import useMediaQuery, { breakpoints } from '../hooks/useMediaQuery';

export interface BatchEditCargoesModalProps extends ModalProps {
  orders?: ScenarioOrder[];
}

export type BatchEditCargoesModalResult = void;

function BatchEditCargoesModal({
  isOpen,
  orders,
  onClose,
}: BatchEditCargoesModalProps): JSX.Element {
  const formRef = useRef<HTMLFormElement | null>(null);
  const currentUser = useCurrentUser();

  const organizationId = currentUser?.organizationId ?? null;

  const notify = useNotify({
    toastId: ' BATCH_EDIT_CARGOES_MODAL',
    type: 'info',
  });

  const { data, refetch } = useVoyageCharterFormQuery();

  const [vcAndOrdersMutation] = useVcAndOrdersMutation({
    refetchQueries: ['VoyageCharter'],
  });

  const [loadPortTimezone, setLoadPortTimezone] = useState<string | undefined>(
    'UTC',
  );

  const form = useForm({
    resolver: zodResolver(batchEditFieldsSchema),
    defaultValues: {
      contract: { type: 'inquiry' },
      dischargePortId: undefined,

      loadPortId: undefined,
      assortmentId: null,
      cargoAmount: null,
      cargoUnitId: null,
      loadingRate: null,
      loadingUnit: null,
      dischargingRate: null,
      dischargingUnit: null,
      marginAmount: null,
      marginUnit: null,
      chartererOrgId: null,
      layday: null,
      cancelling: null,
    },
  });

  // checkbox selection states
  const [selectedFields, setSelectedFields] = useState({
    contractAndStatus: false,
    dischargePort: false,
    loadPort: false,
    assortmentId: false,
    cargoAmountAndUnit: false,
    loadingRateAndUnit: false,
    dischargingRateAndUnit: false,
    marginAmountAndUnit: false,
    charterer: false,
    laydayAndCancelling: false,
  });

  const { register, control, watch, handleSubmit, setValue, getValues } =
    form as UseFormReturn<BatchEditFields>; // Cast is required due to issue in react-hook-form (https://github.com/react-hook-form/react-hook-form/issues/6726)

  const assortments = useMemo(
    () => compact(data?.allAssortments?.nodes),
    [data?.allAssortments?.nodes],
  );

  const assortmentOptions = useMemo(
    () =>
      assortments
        .map((assortment) => ({
          value: assortment.id,
          label: assortment.name,
          isStowageFactor:
            !!assortment.stowageFactorMin || !!assortment.stowageFactorMax,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [assortments],
  );

  const shouldUseReducedUnits =
    currentUser?.rootOrganization?.configReducedUnits ?? false;

  const cargoUnits = useMemo(
    () =>
      compact(
        data?.allCargoUnits?.nodes.filter((unit) => {
          if (shouldUseReducedUnits) {
            return reducedUnits.includes(unit?.name ?? '');
          }
          return unit;
        }),
      ),
    [data?.allCargoUnits?.nodes, shouldUseReducedUnits],
  );

  const cargoUnitOptions = useMemo(
    () =>
      cargoUnits
        .map((cargoUnit) => ({
          value: cargoUnit.id,
          label: cargoUnit.name ?? '',
          name: cargoUnit.name ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [cargoUnits],
  );

  const charterers = useMemo(
    () => compact(data?.charterers?.nodes),
    [data?.charterers?.nodes],
  );

  const chartererOptions = nodesToOptions(charterers);
  const coas = compact(
    data?.allCoaContracts?.nodes.filter((coa) => !coa?.inactive),
  );

  const [createCharterer, { loading: createChartererLoading }] =
    useCreateChartererMutation();

  const handleCreateCharterer = useCallback(
    async (inputValue: string) => {
      const { data } = await createCharterer({
        variables: {
          input: {
            organization: {
              name: inputValue,
              charterer: true,
              parentOrganizationId: organizationId,
            },
          },
        },
      });

      const { id, name } = { ...data?.createOrganization?.organization };

      if (id) {
        setValue('chartererOrgId', {
          value: id,
          label: name ?? '',
        });
      } else {
        console.error('No id received for created charterer');
        toast.error('Error selecting created charterer, please reload page');
      }

      await refetch();
    },
    [createCharterer, organizationId, refetch, setValue],
  );

  const onLaycanChange = (changedField: 'layday' | 'cancelling') => {
    if (!loadPortTimezone) return;

    const { layday, cancelling } = getValues();

    if (changedField === 'layday' && layday !== null) {
      setValue(
        'cancelling',
        moment(layday)
          .add(DEFAULT_LAYCAN_DAYS - 1, 'days')
          .endOf('day')
          .toDate(),
      );
    }

    if (
      changedField === 'cancelling' &&
      cancelling &&
      (!layday || !moment(cancelling).isAfter(moment(layday)))
    ) {
      setValue(
        'layday',
        moment(cancelling)
          .subtract(DEFAULT_LAYCAN_DAYS - 1, 'days')
          .startOf('day')
          .toDate(),
      );
    }
  };

  const handleContractChange = (newContract: ContractDetailsSchema) => {
    setValue(`contract`, newContract);

    if (newContract.type === 'coa' && newContract.coaId) {
      const selectedCoa = coas.find((c) => c.id === newContract.coaId);

      if (!selectedCoa) {
        console.error('Selected COA not found', newContract.coaId);
        return;
      }

      const charterer = selectedCoa.organizationByChartererOrgId;

      if (charterer) {
        setValue(`chartererOrgId`, {
          value: charterer.id,
          label: charterer.name ?? '',
        });
      }
    }
  };

  const getTimezoneForOrder = (
    order: ScenarioOrder,
    formData: BatchEditFields,
  ) => {
    if (selectedFields.loadPort && formData?.loadPortId?.value) {
      return loadPortTimezone;
    } else {
      return order.locationByLoadPortId?.timezone || 'UTC';
    }
  };

  const onSubmit = async (data: BatchEditFields) => {
    if (!currentUser) {
      notify('Current user missing');
      return;
    }

    if (!orders || orders.length === 0) {
      notify('No orders selected for batch edit');
      return;
    }

    try {
      const updateOrders = [];

      const updateVoyageCharters = [];

      const updateVoyageCharterToContracts = [];
      const createVoyageCharterToContracts = [];
      const deleteVoyageCharterToContracts = [];

      const ordersAndValues = [];

      // to keep track of processed voyage charters and contract relations
      const processedVoyageCharters = new Set<number>();
      const processedContractRelations = new Set<string>();

      for (const order of orders) {
        ordersAndValues.push([order, data]);
        const orderPatch: OrderPatch = {};
        const newTimezone = getTimezoneForOrder(order, data) || 'UTC';
        const voyageCharterId =
          order.incomingVoyageCharterRelation?.voyageCharterId;

        if (selectedFields.assortmentId && data.assortmentId) {
          orderPatch.assortmentId = data.assortmentId;
        }
        if (selectedFields.loadPort && data.loadPortId?.value) {
          orderPatch.loadPortId = data.loadPortId.value;
        }
        if (selectedFields.dischargePort && data.dischargePortId?.value) {
          orderPatch.dischargePortId = data.dischargePortId.value;
        }
        if (selectedFields.cargoAmountAndUnit) {
          if (data.cargoAmount) {
            orderPatch.cargoAmount = data.cargoAmount;
          }
          if (data.cargoUnitId) {
            orderPatch.cargoUnitId = data.cargoUnitId;
          }
        }
        if (selectedFields.loadingRateAndUnit) {
          if (data.loadingRate) {
            orderPatch.loadingRate = data.loadingRate;
          }
          if (data.loadingUnit) {
            orderPatch.loadingUnit = data.loadingUnit;
          }
        }

        if (selectedFields.dischargingRateAndUnit) {
          if (data.dischargingRate) {
            orderPatch.dischargingRate = data.dischargingRate;
          }
          if (data.dischargingUnit) {
            orderPatch.dischargingUnit = data.dischargingUnit;
          }
        }

        if (selectedFields.marginAmountAndUnit) {
          if (data.marginAmount) {
            orderPatch.marginAmount = data.marginAmount;
          }
          if (data.marginUnit) {
            orderPatch.marginUnit = data.marginUnit;
          }
        }
        if (selectedFields.charterer && data.chartererOrgId?.value) {
          orderPatch.chartererOrganizationId = data.chartererOrgId.value;
          if (voyageCharterId !== undefined) {
            updateVoyageCharters.push({
              id: voyageCharterId,
              voyageCharterPatch: {
                chartererOrgId: data.chartererOrgId.value,
              },
            });
          }
        }

        if (selectedFields.laydayAndCancelling) {
          if (data.layday && data.cancelling) {
            orderPatch.layday = moment(data.layday)
              .tz(newTimezone)
              .toISOString();

            orderPatch.cancelling = moment(data.cancelling)
              .tz(newTimezone)
              .toISOString();
          } else {
            toast.error('Please, set both layday and cancelling');
            return;
          }
        }

        if (Object.keys(orderPatch).length > 0) {
          updateOrders.push({
            id: order.id,
            orderPatch,
          });
        }

        if (selectedFields.contractAndStatus && data.contract) {
          if (
            voyageCharterId !== undefined &&
            !processedVoyageCharters.has(voyageCharterId)
          ) {
            // to not update the same voyage charter multiple times
            processedVoyageCharters.add(voyageCharterId);
            updateVoyageCharters.push({
              id: voyageCharterId,
              voyageCharterPatch: {
                isInquiry: data.contract.type === 'inquiry',
                isPreliminary:
                  data.contract.type === 'coa' && data.contract.isPreliminary,
                onSubsUntil:
                  data.contract.type === 'spot'
                    ? data.contract.onSubsUntil
                    : null,
              },
            });
          }

          const oldContractRelation =
            order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId
              ?.voyageCharterToContractsByVoyageCharterId?.nodes?.[0] ?? null;

          if (data.contract.type === 'coa' && !oldContractRelation) {
            if (
              voyageCharterId !== undefined &&
              !processedContractRelations.has(`create_${voyageCharterId}`)
            ) {
              // to not update the same contract relation multiple times
              processedContractRelations.add(`create_${voyageCharterId}`);
              createVoyageCharterToContracts.push({
                userId: currentUser?.id,
                voyageCharterId: { existing: voyageCharterId },
                coaId: data.contract.coaId,
              });
            }
          } else if (
            data.contract.type === 'coa' &&
            oldContractRelation &&
            data.contract.coaId !== oldContractRelation.coaId
          ) {
            updateVoyageCharterToContracts.push({
              id: oldContractRelation.id,
              voyageCharterToContractPatch: {
                coaId: data.contract.coaId,
              },
            });
          } else if (data.contract.type !== 'coa' && oldContractRelation) {
            if (
              !processedContractRelations.has(
                `delete_${oldContractRelation.id}`,
              )
            ) {
              // to not update the same contract relation multiple times
              processedContractRelations.add(
                `delete_${oldContractRelation.id}`,
              );
              deleteVoyageCharterToContracts.push(oldContractRelation.id);
            }
          }
        }
      }

      await vcAndOrdersMutation({
        variables: {
          updateOrders,
          updateVoyageCharters,
          createVoyageCharterToContracts,
          updateVoyageCharterToContracts,
          deleteVoyageCharterToContracts,
        },
      });

      toast.success(`Successfully updated ${orders?.length} orders`);
      onClose();
    } catch (error) {
      console.error('Error saving batch edit changes:', error);
      toast.error(`Error saving changes: ${error.toString()}`);
    }
  };
  const fieldLabels: Record<string, string> = {
    contractAndStatus: 'Contract and status',
    loadPort: 'Load port',
    dischargePort: 'Discharge port',
    assortmentId: 'Commodity',
    cargoAmountAndUnit: 'Cargo amount and unit',
    loadingRateAndUnit: 'Loading rate and unit',
    dischargingRateAndUnit: 'Discharging rate and unit',
    marginAmountAndUnit: 'Margin amount and unit',
    charterer: 'Charterer',
    laydayAndCancelling: 'Layday and cancelling',
  };

  const handleCheckboxChange = (
    fieldName: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newSelectedFields = { ...selectedFields };
    newSelectedFields[fieldName as keyof typeof selectedFields] =
      e.target.checked;
    setSelectedFields(newSelectedFields);
  };

  const isMobileView = useMediaQuery(breakpoints.sm);

  const isAnyCheckboxChecked = Object.values(selectedFields).some(
    (field) => field === true,
  );

  return (
    <Modal
      width={isMobileView ? '100%' : '900px'}
      height={isMobileView ? '90vh' : 'auto'}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Form ref={formRef} flex={1} overflowY="auto">
        <Heading level={1} p={4} fontSize={4} fontWeight="bold">
          Batch Edit Cargoes ({orders?.length} selected)
        </Heading>

        {/* Checkboxes */}
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: isMobileView ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
            padding: isMobileView ? '0 18px' : '0 36px',
          }}
          mb={4}
        >
          {Object.keys(fieldLabels).map((key) => (
            <FormField key={key}>
              <Box
                as="label"
                display="flex"
                {...(isMobileView && {
                  width: '130px',
                  justifyContent: 'center',
                })}
              >
                <Input
                  type="checkbox"
                  size={18}
                  checked={selectedFields[key as keyof typeof selectedFields]}
                  onChange={(e) => handleCheckboxChange(key, e)}
                  mr={isMobileView ? 3 : 2}
                />
                <Text {...(isMobileView && { width: '85px' })}>
                  {fieldLabels[key]}
                </Text>
              </Box>
            </FormField>
          ))}
        </Box>

        {/* Input fields */}
        {isAnyCheckboxChecked && (
          <Box
            mx={4}
            p={4}
            bg="#e7e7e7"
            mb={4}
            display="flex"
            flexDirection="column"
            alignContent="center"
          >
            {selectedFields.contractAndStatus && (
              <BatchEditContractSelect
                coas={coas}
                contract={watch('contract') || { type: 'inquiry' }}
                defaultContract={null}
                onChange={handleContractChange}
                isMobileView={isMobileView}
              />
            )}
            {(selectedFields.loadPort || selectedFields.dischargePort) && (
              <Box display="flex" mb={3}>
                {selectedFields.loadPort && (
                  <FormField label="Load port" width={220}>
                    <Controller
                      control={control}
                      name="loadPortId"
                      render={({ field: { value, onChange, name } }) => (
                        <LocationSelect
                          value={value ?? undefined}
                          name={name}
                          required={true}
                          maxHeight={200}
                          onChange={(newValue) => {
                            onChange(newValue);
                            if (newValue) {
                              const timezone =
                                newValue.location?.timezone ?? 'UTC';
                              setLoadPortTimezone(timezone);
                            }
                          }}
                        />
                      )}
                    />
                  </FormField>
                )}

                {selectedFields.dischargePort && (
                  <FormField label="Discharge port" width={220}>
                    <Controller
                      control={control}
                      name="dischargePortId"
                      render={({ field: { value, onChange, name } }) => (
                        <LocationSelect
                          value={value ?? undefined}
                          name={name}
                          required={true}
                          maxHeight={200}
                          onChange={onChange}
                        />
                      )}
                    />
                  </FormField>
                )}
              </Box>
            )}
            {(selectedFields.assortmentId ||
              selectedFields.cargoAmountAndUnit) && (
              <Box
                display="flex"
                flexDirection={isMobileView ? 'column' : 'row'}
                mb={3}
              >
                {selectedFields.assortmentId && (
                  <FormField
                    label="Commodity"
                    width={isMobileView ? '100%' : 220}
                    {...(isMobileView && { paddingRight: '8px' })}
                  >
                    <AutoCompleteSelect
                      options={assortmentOptions}
                      value={watch('assortmentId')}
                      onChange={(value) => setValue('assortmentId', value)}
                      maxHeight={200}
                    />
                  </FormField>
                )}

                {selectedFields.cargoAmountAndUnit && (
                  <Box display="flex">
                    <FormField label="Cargo amount" flex={1} width={220}>
                      <Input
                        type="number"
                        step="0.001"
                        {...register('cargoAmount', { valueAsNumber: true })}
                      />
                    </FormField>
                    <FormField label="Unit" flex={1} width={220}>
                      <Select
                        options={cargoUnitOptions}
                        {...register('cargoUnitId', { valueAsNumber: true })}
                      />
                    </FormField>
                  </Box>
                )}
              </Box>
            )}

            {selectedFields.loadingRateAndUnit && (
              <Box display="flex" mb={3}>
                <FormField
                  label="Loading rate"
                  {...(isMobileView && { flex: 1 })}
                  width={220}
                >
                  <Input
                    type="number"
                    step="any"
                    {...register('loadingRate', { valueAsNumber: true })}
                  />
                </FormField>
                <FormField
                  label="Loading unit"
                  {...(isMobileView && { flex: 1 })}
                  width={220}
                >
                  <Select
                    options={loadingUnitOptions(shouldUseReducedUnits)}
                    {...register('loadingUnit', {
                      setValueAs: valueOrNull,
                    })}
                  />
                </FormField>
              </Box>
            )}
            {selectedFields.dischargingRateAndUnit && (
              <Box display="flex" mb={3}>
                <FormField
                  label="Discharging rate"
                  {...(isMobileView && { flex: 1 })}
                  width={220}
                >
                  <Input
                    type="number"
                    step="any"
                    {...register('dischargingRate', { valueAsNumber: true })}
                  />
                </FormField>
                <FormField
                  label="Discharging unit"
                  {...(isMobileView && { flex: 1 })}
                  width={220}
                >
                  <Select
                    options={loadingUnitOptions(shouldUseReducedUnits)}
                    {...register('dischargingUnit', {
                      setValueAs: valueOrNull,
                    })}
                  />
                </FormField>
              </Box>
            )}
            {(selectedFields.marginAmountAndUnit ||
              selectedFields.charterer) && (
              <Box
                display="flex"
                flexDirection={isMobileView ? 'column' : 'row'}
                mb={3}
              >
                {selectedFields.marginAmountAndUnit && (
                  <Box display="flex">
                    <FormField label="Margin" flex={1} width={220}>
                      <Input
                        type="number"
                        {...register('marginAmount', { valueAsNumber: true })}
                      />
                    </FormField>
                    <FormField label="Margin unit" flex={1} width={220}>
                      <Select
                        options={marginUnits}
                        {...register('marginUnit')}
                      />
                    </FormField>
                  </Box>
                )}

                {selectedFields.charterer && (
                  <FormField
                    label="Charterer"
                    width={isMobileView ? '100%' : 220}
                    {...(isMobileView && { paddingRight: '8px' })}
                  >
                    <Controller
                      name="chartererOrgId"
                      control={control}
                      render={({ field }) => (
                        <CreatableSelect
                          isLoading={createChartererLoading}
                          isDisabled={createChartererLoading}
                          styles={reactSelectStyles}
                          options={chartererOptions}
                          onCreateOption={handleCreateCharterer}
                          menuPosition="fixed"
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                )}
              </Box>
            )}

            {selectedFields.laydayAndCancelling && (
              <Box display="flex" mb={3}>
                <FormField label="Layday" width={220}>
                  <Controller
                    defaultValue={null}
                    control={control}
                    name={`layday`}
                    render={({ field: { value, onChange } }) => (
                      <DatePicker
                        showTime
                        showTimeInput
                        onChange={(newValue) => {
                          onChange(newValue);
                          onLaycanChange('layday');
                        }}
                        selected={value}
                        defaultTimePickerType="START_OF_DAY"
                        timeZone="UTC"
                      />
                    )}
                  />
                </FormField>

                <FormField label="Cancelling" width={220}>
                  <Controller
                    defaultValue={null}
                    control={control}
                    name={`cancelling`}
                    render={({ field: { value, onChange } }) => (
                      <DatePicker
                        showTime
                        showTimeInput
                        onChange={(newValue) => {
                          onChange(newValue);
                          onLaycanChange('cancelling');
                        }}
                        selected={value}
                        defaultTimePickerType="END_OF_DAY"
                        timeZone="UTC"
                      />
                    )}
                  />
                </FormField>
              </Box>
            )}
          </Box>
        )}
      </Form>
      <ModalFooter>
        <Button variant="secondary" onPress={onClose} mr={3}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            handleSubmit(onSubmit)();
          }}
        >
          Save Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default BatchEditCargoesModal;
